// @ts-nocheck
import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Scale,
  Users,
  Calendar,
  Wallet,
  CheckSquare,
  Clock,
  TrendingUp,
  AlertTriangle,
  ArrowRight,
  Sparkles,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { MobileStatCard } from '../ui/MobileStatCard';
import { MobileSection } from '../ui/MobileSection';
import { MobileListItem } from '../ui/MobileListItem';
import { MobileEmptyState } from '../ui/MobileEmptyState';
import { MobilePageContainer } from '../ui/MobilePageContainer';

const MobileDashboard = () => {
  const navigate = useNavigate();
  const { user, profile } = useAuth();

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    activeCases: 0,
    clients: 0,
    pendingTasks: 0,
    upcomingAppointments: 0,
    openToReceive: 0,
    openToPay: 0,
  });
  const [upcoming, setUpcoming] = useState<
    Array<{
      id: string;
      kind: 'appointment' | 'task';
      title: string;
      when: Date;
      url: string;
      priority?: string;
    }>
  >([]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    const run = async () => {
      setLoading(true);
      try {
        const now = new Date();
        const in7Days = new Date(now.getTime() + 7 * 24 * 3600 * 1000);

        const [
          casesRes,
          clientsRes,
          tasksRes,
          apptsRes,
          finRes,
          soonApptsRes,
          soonTasksRes,
        ] = await Promise.all([
          supabase
            .from('cases')
            .select('id', { count: 'exact', head: true })
            .eq('user_id', user.id)
            .neq('archived', true),
          supabase
            .from('clients')
            .select('id', { count: 'exact', head: true })
            .eq('user_id', user.id),
          supabase
            .from('tasks')
            .select('id', { count: 'exact', head: true })
            .eq('user_id', user.id)
            .neq('status', 'Concluída'),
          supabase
            .from('appointments')
            .select('id', { count: 'exact', head: true })
            .eq('user_id', user.id)
            .gte('appointment_date', now.toISOString())
            .lte('appointment_date', in7Days.toISOString()),
          supabase
            .from('financial_entries')
            .select('amount, entry_type, payment_confirmed')
            .eq('user_id', user.id)
            .is('payment_date', null),
          supabase
            .from('appointments')
            .select('id, title, appointment_date')
            .eq('user_id', user.id)
            .gte('appointment_date', now.toISOString())
            .order('appointment_date', { ascending: true })
            .limit(3),
          supabase
            .from('tasks')
            .select('id, title, due_date, case_id, priority, created_at')
            .eq('user_id', user.id)
            .neq('status', 'Concluída')
            .order('due_date', { ascending: true, nullsFirst: false })
            .limit(20),
        ]);

        if (cancelled) return;

        let openToReceive = 0;
        let openToPay = 0;
        (finRes.data || []).forEach((e: any) => {
          if (e.payment_confirmed) return;
          const amt = Number(e.amount) || 0;
          if (e.entry_type === 'income' || e.entry_type === 'receita') {
            openToReceive += amt;
          } else {
            openToPay += amt;
          }
        });

        setStats({
          activeCases: casesRes.count || 0,
          clients: clientsRes.count || 0,
          pendingTasks: tasksRes.count || 0,
          upcomingAppointments: apptsRes.count || 0,
          openToReceive,
          openToPay,
        });

        const appts = (soonApptsRes.data || []).map((a: any) => ({
          id: a.id,
          kind: 'appointment' as const,
          title: a.title,
          when: new Date(a.appointment_date),
          url: '/pwa/agenda',
        }));
        const tsks = (soonTasksRes.data || []).map((t: any) => ({
          id: t.id,
          kind: 'task' as const,
          title: t.title,
          // pendente sem prazo também entra com baixa prioridade visual
          when: t.due_date ? new Date(t.due_date) : new Date(t.created_at || now.toISOString()),
          url: t.case_id ? `/pwa/processo/${t.case_id}` : '/pwa/tasks',
          priority: t.priority,
          hasDueDate: Boolean(t.due_date),
        }));
        const priorityRank = (p?: string) => {
          const v = String(p || '').toLowerCase();
          if (v.includes('crít') || v.includes('urgent') || v.includes('urg')) return 0;
          if (v.includes('alta') || v.includes('high')) return 1;
          if (v.includes('méd') || v.includes('media') || v.includes('medium')) return 2;
          return 3;
        };
        const merged = [...appts, ...tsks]
          .sort((a, b) => {
            // Tarefas urgentes/alta sobem no dashboard.
            if (a.kind === 'task' && b.kind === 'task') {
              const pa = priorityRank(a.priority);
              const pb = priorityRank(b.priority);
              if (pa !== pb) return pa - pb;
              // tarefas com prazo vêm antes das sem prazo
              const ad = (a as any).hasDueDate ? 0 : 1;
              const bd = (b as any).hasDueDate ? 0 : 1;
              if (ad !== bd) return ad - bd;
            }
            return a.when.getTime() - b.when.getTime();
          })
          .slice(0, 5);
        setUpcoming(merged);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const greeting = useMemo(() => {
    const h = new Date().getHours();
    if (h < 12) return 'Bom dia';
    if (h < 18) return 'Boa tarde';
    return 'Boa noite';
  }, []);

  const firstName = (profile?.full_name || user?.email || '').split(' ')[0];

  const fmtMoney = (n: number) =>
    n.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      maximumFractionDigits: 0,
    });

  const fmtDateTime = (d: Date) =>
    d.toLocaleString('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });

  return (
    <MobilePageContainer>
      {/* Saudação */}
      <div className="px-4 pt-5 pb-4">
        <p className="text-[11px] uppercase tracking-[0.18em] text-[#a88a5c]">{greeting},</p>
        <h1 className="mt-0.5 text-[26px] font-semibold tracking-tight truncate text-slate-900">
          {firstName || 'Advogado(a)'}
        </h1>
      </div>

      {/* Grid de KPIs */}
      <div className="grid grid-cols-2 gap-2 px-3 mb-4">
        <MobileStatCard
          label="Processos ativos"
          value={loading ? '—' : stats.activeCases}
          icon={<Scale className="h-4 w-4" />}
          tone="primary"
          onClick={() => navigate('/pwa/cases')}
        />
        <MobileStatCard
          label="Clientes"
          value={loading ? '—' : stats.clients}
          icon={<Users className="h-4 w-4" />}
          tone="info"
          onClick={() => navigate('/pwa/clients')}
        />
        <MobileStatCard
          label="Tarefas pendentes"
          value={loading ? '—' : stats.pendingTasks}
          icon={<CheckSquare className="h-4 w-4" />}
          tone="warning"
          onClick={() => navigate('/pwa/tasks')}
        />
        <MobileStatCard
          label="Próx. 7 dias"
          value={loading ? '—' : stats.upcomingAppointments}
          icon={<Calendar className="h-4 w-4" />}
          tone="success"
          hint="compromissos"
          onClick={() => navigate('/pwa/agenda')}
        />
      </div>

      {/* Financeiro em destaque */}
      <div className="grid grid-cols-2 gap-2 px-3 mb-5">
        <MobileStatCard
          label="A receber"
          value={loading ? '—' : fmtMoney(stats.openToReceive)}
          icon={<TrendingUp className="h-4 w-4" />}
          tone="success"
          onClick={() => navigate('/pwa/financial')}
        />
        <MobileStatCard
          label="A pagar"
          value={loading ? '—' : fmtMoney(stats.openToPay)}
          icon={<AlertTriangle className="h-4 w-4" />}
          tone="danger"
          onClick={() => navigate('/pwa/financial')}
        />
      </div>

      {/* Próximos */}
      <MobileSection
        title={`Próximos (${loading ? '…' : upcoming.length})`}
        noCard
        action={
          <button
            className="text-[#a88a5c] font-medium inline-flex items-center gap-1"
            onClick={() => navigate('/pwa/agenda')}
          >
            Ver tudo <ArrowRight className="h-3 w-3" />
          </button>
        }
      >
        {(() => {
          const todayStart = new Date();
          todayStart.setHours(0, 0, 0, 0);
          const todayEnd = new Date();
          todayEnd.setHours(23, 59, 59, 999);
          const todays = upcoming.filter(
            (ev) => ev.when.getTime() >= todayStart.getTime() && ev.when.getTime() <= todayEnd.getTime(),
          );
          const tasksToday = todays.filter((ev) => ev.kind === 'task');
          const appointmentsToday = todays.filter((ev) => ev.kind === 'appointment');
          const deadlinesToday = tasksToday.filter((ev: any) => Boolean((ev as any).url?.includes('/processo/')));

          const headline =
            todays.length === 0
              ? 'Olá, Doutora. Sem compromissos para hoje.'
              : `Olá, Doutora. Para hoje temos ${tasksToday.length} tarefa(s), ${appointmentsToday.length} atendimento(s) e ${deadlinesToday.length} prazo(s).`;

          return (
            <div className="mx-3 mb-2 rounded-2xl border border-[#c4ab83]/35 bg-slate-900 px-4 py-3 shadow-[0_16px_34px_-26px_rgba(15,23,42,0.8)]">
              <p className="text-[10px] uppercase tracking-[0.16em] text-[#c4ab83] mb-1">Assistente de lembretes</p>
              <p className="text-[13px] leading-relaxed text-slate-100">{headline}</p>
            </div>
          );
        })()}

        {upcoming.some(
          (ev) =>
            ev.kind === 'task' &&
            (String(ev.priority || '').toLowerCase().includes('alta') ||
              String(ev.priority || '').toLowerCase().includes('urg')),
        ) && (
          <div className="mx-3 mb-2 rounded-xl border border-rose-300/40 bg-gradient-to-r from-rose-500/10 via-amber-400/10 to-transparent px-3 py-2 shadow-[0_10px_30px_-22px_rgba(244,63,94,0.65)]">
            <p className="text-[11px] font-semibold tracking-wide text-rose-700 dark:text-rose-300 inline-flex items-center gap-1.5">
              <Sparkles className="h-3.5 w-3.5" />
              Prioridades críticas em destaque
            </p>
          </div>
        )}

        {upcoming.length === 0 ? (
          <div className="mx-3 rounded-2xl border border-border bg-card overflow-hidden py-6">
            <MobileEmptyState
              icon={<Clock className="h-5 w-5" />}
              title="Sem compromissos próximos"
              description="Você está tranquilo por agora."
            />
          </div>
        ) : (
          <div className="mx-3 rounded-2xl border border-border bg-card overflow-hidden shadow-[0_12px_28px_-22px_rgba(0,0,0,0.25)]">
            <div className="max-h-[300px] overflow-y-auto scrollbar-hide divide-y divide-border">
            {upcoming.map((ev) => {
              const isOverdueTask =
                ev.kind === 'task' && ev.when.getTime() < Date.now();
              const isHighPriorityTask =
                ev.kind === 'task' &&
                (String(ev.priority || '').toLowerCase().includes('alta') ||
                  String(ev.priority || '').toLowerCase().includes('urg'));

              return (
                <MobileListItem
                  key={`${ev.kind}-${ev.id}`}
                  icon={
                    ev.kind === 'appointment' ? (
                      <Calendar className="h-5 w-5" />
                    ) : (
                      <CheckSquare className="h-5 w-5" />
                    )
                  }
                  iconBg={
                    isOverdueTask
                      ? 'bg-rose-500/20 text-rose-700 dark:text-rose-300'
                      : ev.kind === 'appointment'
                        ? 'bg-[#faf7f2] text-[#a88a5c] ring-1 ring-[#c4ab83]/25'
                        : isHighPriorityTask
                          ? 'bg-rose-500/15 text-rose-700'
                          : 'bg-slate-100 text-slate-600'
                  }
                  title={ev.title}
                  subtitle={
                    ev.kind === 'appointment'
                      ? 'Atendimento'
                      : isOverdueTask
                        ? `Tarefa • Atrasada${ev.priority ? ` • ${ev.priority}` : ''}`
                        : isHighPriorityTask
                          ? `Tarefa • ${ev.priority || 'Alta prioridade'}`
                          : 'Tarefa'
                  }
                  badges={
                    isOverdueTask ? (
                      <span className="inline-flex items-center gap-1 rounded-full border border-rose-500/30 bg-rose-500/10 px-2 py-0.5 text-[10px] font-semibold text-rose-700 dark:text-rose-300">
                        <AlertTriangle className="h-3 w-3" />
                        Atrasada
                      </span>
                    ) : undefined
                  }
                  meta={fmtDateTime(ev.when)}
                  className={
                    isOverdueTask
                      ? 'relative border-l-4 border-rose-500 bg-gradient-to-r from-rose-500/[0.09] to-transparent'
                      : isHighPriorityTask
                        ? 'relative border-l-4 border-amber-500 bg-gradient-to-r from-amber-500/[0.08] to-transparent'
                        : ''
                  }
                  onClick={() => navigate(ev.url)}
                />
              );
            })}
            </div>
          </div>
        )}
      </MobileSection>

      {/* Atalhos */}
      <MobileSection title="Acesso rápido">
        <MobileListItem
          icon={<Scale className="h-5 w-5" />}
          iconBg="bg-[#faf7f2] text-[#a88a5c] ring-1 ring-[#c4ab83]/25"
          title="Processos"
          subtitle="Casos judiciais e administrativos"
          onClick={() => navigate('/pwa/cases')}
        />
        <MobileListItem
          icon={<Users className="h-5 w-5" />}
          iconBg="bg-[#faf7f2] text-[#a88a5c] ring-1 ring-[#c4ab83]/25"
          title="Clientes"
          subtitle="Base de contatos"
          onClick={() => navigate('/pwa/clients')}
        />
        <MobileListItem
          icon={<Calendar className="h-5 w-5" />}
          iconBg="bg-[#faf7f2] text-[#a88a5c] ring-1 ring-[#c4ab83]/25"
          title="Agenda"
          subtitle="Compromissos e prazos"
          onClick={() => navigate('/pwa/agenda')}
        />
        <MobileListItem
          icon={<Wallet className="h-5 w-5" />}
          iconBg="bg-[#faf7f2] text-[#a88a5c] ring-1 ring-[#c4ab83]/25"
          title="Financeiro"
          subtitle="Receber e pagar"
          onClick={() => navigate('/pwa/financial')}
        />
      </MobileSection>
    </MobilePageContainer>
  );
};

export default MobileDashboard;
